import { Action } from 'redux';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { ContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/ContractNegotiationVersionConcept';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractState, ContractUnloadState } from '../../states/ContractState';

const FindContract = (currentState: ContractState, contractId: number): Contract | undefined =>
  currentState.list?.find((item: Contract) => item.id === contractId);

const UpdateContractInList = (currentState: ContractState, updatedContract?: Contract): Contract[] => {
  if (!updatedContract) return currentState.list || [];
  return (currentState.list || []).map((item: Contract) =>
    item.id === updatedContract.id ? updatedContract : item
  );
};

const BuildState = (currentState: ContractState, updatedContract?: Contract): ContractState => ({
  ...currentState,
  list: UpdateContractInList(currentState, updatedContract),
});

const UpsertVersionConcept = (
  concepts: ContractNegotiationVersionConcept[] | undefined,
  versionConcept: ContractNegotiationVersionConcept
): ContractNegotiationVersionConcept[] => {
  const current = concepts || [];
  const exists = current.some((c: ContractNegotiationVersionConcept) => c.id === versionConcept.id);
  if (!exists) return [...current, versionConcept];
  return current.map((c: ContractNegotiationVersionConcept) => (c.id === versionConcept.id ? versionConcept : c));
};

const UpdateVersionInNegotiation = (
  neg: ContractNegotiation,
  versionId: string,
  updater: (v: ContractNegotiationVersion) => ContractNegotiationVersion
): ContractNegotiation => ({
  ...neg,
  versions: (neg.versions || []).map((v: ContractNegotiationVersion) =>
    v.id === versionId ? updater(v) : v
  ),
});

const UpdateNegotiationInContract = (
  contract: Contract,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): Contract => ({
  ...contract,
  negotiations: (contract.negotiations || []).map((n: ContractNegotiation) =>
    n.id === negotiationId ? updater(n) : n
  ),
  addendums: (contract.addendums || []).map((addendum: ContractAddendum) => ({
    ...addendum,
    negotiations: (addendum.negotiations || []).map((n: ContractNegotiation) =>
      n.id === negotiationId ? updater(n) : n
    ),
  } as ContractAddendum)),
});

const HandleNegotiationUpdate = (
  currentState: ContractState,
  contractId: number,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): ContractState => {
  const contract = FindContract(currentState, contractId);
  if (!contract) return currentState;
  return BuildState(currentState, UpdateNegotiationInContract(contract, negotiationId, updater));
};

const HandleVersionConceptUpsert = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  versionConcept: ContractNegotiationVersionConcept
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg) =>
    UpdateVersionInNegotiation(neg, versionConcept.contractNegotiationVersionId, (v) => ({
      ...v,
      concepts: UpsertVersionConcept(v.concepts, versionConcept),
    }))
  );

const HandleVersionConceptStatus = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  versionConcept: ContractNegotiationVersionConcept,
  partial: Partial<ContractNegotiationVersionConcept>
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg) =>
    UpdateVersionInNegotiation(neg, versionConcept.contractNegotiationVersionId, (v) => ({
      ...v,
      concepts: (v.concepts || []).map((c: ContractNegotiationVersionConcept) =>
        c.id === versionConcept.id ? ({ ...c, ...partial } as ContractNegotiationVersionConcept) : c
      ),
    }))
  );

const HandleVersionConceptDelete = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  contractNegotiationVersionId: string,
  versionConceptId: string
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg) =>
    UpdateVersionInNegotiation(neg, contractNegotiationVersionId, (v) => ({
      ...v,
      concepts: (v.concepts || []).filter((c: ContractNegotiationVersionConcept) => c.id !== versionConceptId),
    }))
  );

const HandleVersionFlags = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  contractNegotiationVersionId: string,
  partial: Partial<ContractNegotiationVersion>
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg) =>
    UpdateVersionInNegotiation(neg, contractNegotiationVersionId, (v) => ({
      ...v,
      ...partial,
    } as ContractNegotiationVersion))
  );

const Handlers: Record<string, (currentState: ContractState, action: any) => ContractState> = {
  CONTRACTNEGOTIATIONVERSIONCONCEPT_GET_ALL_REQUEST_ACTION: (currentState, action) =>
    HandleVersionFlags(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionId, {
      isGettingVersionConcepts: true,
      isGettingVersionConceptsSuccess: false,
      FailGettingVersionConcepts: false,
      ErrorGettingVersionConcepts: undefined,
    }),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_GET_ALL_RECEIVE_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) =>
      UpdateVersionInNegotiation(neg, action.contractNegotiationVersionId, (v) => ({
        ...v,
        concepts: action.contractNegotiationVersionConcepts,
        isGettingVersionConcepts: false,
        isGettingVersionConceptsSuccess: true,
        FailGettingVersionConcepts: false,
        ErrorGettingVersionConcepts: undefined,
      } as ContractNegotiationVersion))
    ),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_GET_ALL_ERROR_ACTION: (currentState, action) =>
    HandleVersionFlags(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionId, {
      isGettingVersionConcepts: false,
      isGettingVersionConceptsSuccess: false,
      FailGettingVersionConcepts: true,
      ErrorGettingVersionConcepts: action.error,
    }),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_GET_BY_ID_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionConceptUpsert(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionConcept),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_CREATE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionConceptUpsert(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionConcept),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_UPDATE_REQUEST_ACTION: (currentState, action) =>
    HandleVersionConceptStatus(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionConcept, {
      isUpdating: true,
      isUpdatedSuccessfully: undefined,
      failOnUpdating: false,
      error: undefined,
    }),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_UPDATE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionConceptUpsert(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionConcept),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_UPDATE_ERROR_ACTION: (currentState, action) =>
    HandleVersionConceptStatus(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionConcept, {
      isUpdating: false,
      isUpdatedSuccessfully: false,
      failOnUpdating: true,
      error: action.error,
    }),
  CONTRACTNEGOTIATIONVERSIONCONCEPT_DELETE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionConceptDelete(currentState, action.contractId, action.contractNegotiationId, action.contractNegotiationVersionId, action.id),
};

export default function ContractNegotiationVersionConceptsReducer(
  state: ContractState | undefined,
  action: Action
): ContractState {
  const currentState = state ?? ContractUnloadState;
  const a: any = action;
  const handler = Handlers[a.type];
  if (!handler) return currentState;
  return handler(currentState, a);
}
